import { useEffect, useReducer } from "react";
import {
  collection,
  onSnapshot,
  query,
  where,
  type FirestoreError,
  type QuerySnapshot,
} from "firebase/firestore";
import { firebaseDB } from "@lemonadetraining/web-shared";
import { isMovementRegistryEntry, type MovementRegistryEntry } from "@lemonadetraining/contracts";

export type MovementRegistryStatus = "idle" | "loading" | "ready" | "error";

export interface UseMovementRegistryResult {
  status: MovementRegistryStatus;
  movements: MovementRegistryEntry[];
  error: Error | null;
}

export interface UseMovementRegistryOptions {
  enabled?: boolean;
}

type Action =
  | { type: "loading" }
  | { type: "ready"; movements: MovementRegistryEntry[] }
  | { type: "error"; error: Error };

function reducer(state: UseMovementRegistryResult, action: Action): UseMovementRegistryResult {
  switch (action.type) {
    case "loading":
      return { ...state, status: "loading", error: null };
    case "ready":
      return { status: "ready", movements: action.movements, error: null };
    case "error":
      return { ...state, status: "error", error: action.error };
  }
}

const initialState: UseMovementRegistryResult = { status: "idle", movements: [], error: null };

/**
 * Subscribes to global movement registry entries (`MovementRegistry/`, `boxID == null`).
 *
 * V1 reads only the global tier — box overrides are out of scope for LT-314.
 * Filters and validates each doc via `isMovementRegistryEntry`; malformed
 * entries are dropped silently rather than failing the whole listener.
 *
 * Pass `{ enabled: false }` to keep the hook idle and avoid opening a Firestore
 * listener until a consumer signals they need the data. Used by
 * `MovementRegistryProvider` to defer the ~127-doc fetch until the user opens
 * an axis drill-down, movement search, or movement detail page.
 */
export function useMovementRegistry(
  options: UseMovementRegistryOptions = {}
): UseMovementRegistryResult {
  const { enabled = true } = options;
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    if (!enabled) return;
    dispatch({ type: "loading" });
    const ref = collection(firebaseDB, "MovementRegistry");
    const q = query(ref, where("boxID", "==", null));
    const unsubscribe = onSnapshot(
      q,
      (snap: QuerySnapshot) => {
        const items: MovementRegistryEntry[] = [];
        snap.forEach((doc) => {
          const data = { id: doc.id, ...doc.data() };
          if (isMovementRegistryEntry(data)) items.push(data);
        });
        dispatch({ type: "ready", movements: items });
      },
      (err: FirestoreError) => dispatch({ type: "error", error: err })
    );
    return () => unsubscribe();
  }, [enabled]);

  return state;
}
